import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import WarningIcon from '@material-ui/icons/Warning';
import React, {
  memo,
  MouseEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { useSnapshots } from '../context/SnapshotsContext';

const useStyles = makeStyles(() => ({
  title: {
    fontWeight: 700,
  },
  graphContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
}));

const Tick = (props: any) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={-25} y={0} dy={16} fill="#161d42" fontSize={12} fontWeight="600">
        {payload.value.split(' ').map((item: string) => (
          <tspan x={-25} dy="1.2em">
            {item}
          </tspan>
        ))}
      </text>
    </g>
  );
};

interface IAdhererenceGraphProps {
  data: any[];
  title: string;
  onOpenDialogCloseToExpiration?: () => void;
  fillColor: string;
}

const AdherenceGraph = (props: IAdhererenceGraphProps) => {
  const { data, title, fillColor, onOpenDialogCloseToExpiration } = props;
  const { redirectWithSnap } = useSnapshots();

  const classes = useStyles();
  const [avg, setAvg] = useState<{
    adherence: number;
    name: any;
    id: any;
  }>({ id: 0, name: process.env.REACT_APP_NAME || 'SolidFiles', adherence: 0 });

  const labelFormatter = useCallback((value: number) => {
    return value.toString().replace('.', ',') + '%';
  }, []);

  const chartData = data
    .sort((a, b) => a.id - b.id)
    .map((item) => ({
      adherence: Math.round(
        (+item.approvedDocuments / +item.activeDocuments) * 100 || 0
      ),
      name: item.ventureName,
      id: item.id,
    }));

  useEffect(() => {
    const sum: number = chartData.reduce(
      (previousValue: number, currentValue: any) => {
        previousValue += currentValue.adherence;
        return previousValue;
      },
      0
    );
    setAvg((avg) => ({
      ...avg,
      adherence: Math.round(sum / chartData.length),
    }));
  }, [chartData]);

  const handleOpenDialogCloseToExpiration: MouseEventHandler<HTMLButtonElement> =
    useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpenDialogCloseToExpiration && onOpenDialogCloseToExpiration();
      },
      [onOpenDialogCloseToExpiration]
    );

  const onClick = useCallback(() => {
    redirectWithSnap('/documentation/dashboard');
  }, [redirectWithSnap]);

  return (
    <Card>
      <CardActionArea component="span" onClick={onClick}>
        <CardHeader
          title={
            <Typography align="center" classes={{ root: classes.title }}>
              {title}
            </Typography>
          }
          action={
            onOpenDialogCloseToExpiration && (
              <IconButton
                onClick={handleOpenDialogCloseToExpiration}
                color="primary"
              >
                <WarningIcon />
              </IconButton>
            )
          }
        />
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              margin={{
                top: 20,
                bottom: 40,
                left: 10,
                right: 10,
              }}
              data={[avg, ...chartData]}
              style={{
                cursor: 'pointer',
              }}
            >
              <XAxis dataKey="name" interval={0} tick={<Tick />} />
              <YAxis hide domain={[0, 100]} allowDecimals />
              <Bar
                isAnimationActive={false}
                dataKey="adherence"
                fill={fillColor}
                label={{
                  formatter: labelFormatter,
                  position: 'top',
                  color: '#161d42',
                }}
                maxBarSize={40}
                radius={6}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default memo(AdherenceGraph);
